import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../ui/Card";
import Stat from "../../ui/Stat";
import Progress from "../../ui/Progress";
import Table from "../../ui/Table";
import { fetchOrgSeatSnapshot } from "../../utils/orgData";
import { fetchOrgUsers } from "../../utils/orgUsers";
import { useAuth } from "../../context/AuthContext";

export default function SeatUsagePage() {
  const nav = useNavigate();
  const { user, activeOrgId } = useAuth();
  const isReadOnly = user?.role === "investor";
  const [stats, setStats] = useState({
    seatsUsed: 0,
    seatsTotal: 30,
    roles: { coach: 0, admin: 0, member: 0, superadmin: 0 },
  });

  useEffect(() => {
    let mounted = true;
    const orgId = activeOrgId || "org_default";
    Promise.all([fetchOrgSeatSnapshot(user, orgId, 30), fetchOrgUsers(user, orgId)])
      .then(([seatSnapshot, orgUsers]) => {
        if (!mounted) return;
        const seatsTotal = seatSnapshot?.seatsTotal || 30;
        const activeUsers = (orgUsers || []).filter(
          (u) => !u.disabled && String(u.status || "").toLowerCase() !== "pending"
        );
        const counts = activeUsers.reduce(
          (acc, u) => {
            const role = String(u.role || "member").toLowerCase();
            if (role.includes("superadmin")) acc.superadmin += 1;
            else if (role.includes("admin")) acc.admin += 1;
            else if (role.includes("coach")) acc.coach += 1;
            else acc.member += 1;
            return acc;
          },
          { coach: 0, admin: 0, member: 0, superadmin: 0 }
        );
        const seatsUsed = seatSnapshot?.seatsUsed ?? activeUsers.filter(
          (u) => !String(u.role || "").toLowerCase().includes("superadmin")
        ).length;
        setStats({
          seatsUsed,
          seatsTotal,
          roles: counts,
        });
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [user, activeOrgId]);

  const pct = Math.min(
    100,
    Math.round(((stats.seatsUsed || 0) / Math.max(1, stats.seatsTotal || 1)) * 100)
  );

  const roleHeaders = ["Role", "Count"];
  const roleRows = [
    ["Superadmin", stats.roles?.superadmin ?? 0],
    ["Admin", stats.roles?.admin ?? 0],
    ["Coach", stats.roles?.coach ?? 0],
    ["Member", stats.roles?.member ?? 0],
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <div className="flex items-end justify-between">
          <Stat label="Seats Used" value={`${stats.seatsUsed ?? 0}/${stats.seatsTotal ?? 30}`} />
          <button className="btn btn-ghost" onClick={() => nav("/org/invite")} disabled={isReadOnly}>
            Invite Users
          </button>
        </div>
        <div className="mt-4">
          <Progress value={pct} />
          <div className="text-sm mt-2 opacity-70">{pct}% of plan used</div>
        </div>
      </Card>

      <Card>
        <Stat label="By Role" value="Users per role" />
        <div className="mt-3">
          <Table headers={roleHeaders} rows={roleRows} />
        </div>
      </Card>
    </div>
  );
}
